import { css } from '@emotion/react'
import { Button, Flow, Icon } from 'bold-ui'
import {
  EquipeSelectField,
  Form,
  PecFormApi,
  resolveName,
  SEM_EQUIPE_ITEM,
  SubmitButton,
  UnidadeSaudeSelectField,
} from 'components/form'
import { MicroareaImovelSelectField } from 'components/form/field/select/MicroareaImovelSelectField/MicroareaImovelSelectField'
import { TipoEnderecoEnum } from 'graphql/types.generated'
import { generatePath, useHistory, useRouteMatch } from 'react-router'
import { metaPath } from 'util/metaPath'
import { MicroareasFilterModal } from 'view/visualizacaoTerritorial/components/filter/MicroareasFilterModal'
import { TerritorioViewTabEnum, VisualizacaoMicroareasFilterFormModel } from 'view/visualizacaoTerritorial/model'
import { validateVisualizacaoTerritorialForm } from 'view/visualizacaoTerritorial/validator'

import {
  FILTROS_AVANCADOS_MODAL_ROUTE,
  MAPA_SIDEBAR_NAV_ITEM_ROUTE,
  MapaSidebarNavItemEnum,
} from '../../model-territorioMapa'
import { createTerritorioMapaBuscaFormCalculator } from '../calculator-territorioMapaBuscaForm'
import { TerritorioMapaBuscaFormModel } from '../model-territorioMapaBusca'
import { isFilterDefault } from '../utils-territorioMapaBusca'

const meta = metaPath<TerritorioMapaBuscaFormModel>()

const SEM_INFORMACAO = SEM_EQUIPE_ITEM.ine

interface TerritorioMapaBuscaFormProps {
  defaultValues: TerritorioMapaBuscaFormModel
  initialValues: TerritorioMapaBuscaFormModel
  onSubmit: (values: TerritorioMapaBuscaFormModel) => void
}

export function TerritorioMapaBuscaForm(props: Readonly<TerritorioMapaBuscaFormProps>) {
  const { defaultValues, initialValues, onSubmit } = props

  const match = useRouteMatch<{ filtrosAvancados?: string }>()
  const history = useHistory()
  const { url } = useRouteMatch()

  const handleReset = (form: PecFormApi<TerritorioMapaBuscaFormModel>) => {
    onSubmit(defaultValues)
    form.reset()
  }

  const handleOpenFiltrosAvancados = () =>
    history.push(
      generatePath(match.path, {
        mapaSidebarNavItem: MAPA_SIDEBAR_NAV_ITEM_ROUTE[MapaSidebarNavItemEnum.BUSCA],
        filtrosAvancados: FILTROS_AVANCADOS_MODAL_ROUTE,
      })
    )

  const handleCloseFiltrosAvancados = () => {
    history.push(url.replace(`/${FILTROS_AVANCADOS_MODAL_ROUTE}`, ''))
  }

  const handleFiltrosAvancadosSubmit = (
    form: PecFormApi<TerritorioMapaBuscaFormModel>,
    values: VisualizacaoMicroareasFilterFormModel
  ) => {
    form.change(resolveName(meta.filtrosAvancados), values)
    form.submit()
    handleCloseFiltrosAvancados()
  }

  return (
    <Form<TerritorioMapaBuscaFormModel>
      render={({ form, values, handleSubmit }) => (
        <Flow direction='vertical' style={styles.root}>
          <Flow direction='vertical'>
            <UnidadeSaudeSelectField name={meta.unidadeSaude} label='Unidade de saúde' required mostrarInativas />
            <EquipeSelectField
              name={meta.equipe}
              label='Equipe'
              unidadeSaudeId={values?.unidadeSaude?.id}
              disabled={!values?.unidadeSaude?.id}
              required
              showSemEquipeOption
              mostrarInativas
            />
            <MicroareaImovelSelectField
              name={meta.microareas}
              label='Microárea'
              placeholder='Todas as microáreas'
              cnes={values.unidadeSaude?.cnes ?? SEM_INFORMACAO}
              ine={values.equipe?.ine ?? SEM_INFORMACAO}
              tipoEndereco={TipoEnderecoEnum.LOGRADOURO}
              disabled={!values?.equipe?.ine}
              multiple
            />
            <Button kind='primary' skin='outline' size='small' block onClick={handleOpenFiltrosAvancados}>
              <Flow direction='horizontal' gap={0.5}>
                <Icon icon='filterOutline' />
                Filtros avançados
              </Flow>
            </Button>
            {!!match.params.filtrosAvancados && (
              <MicroareasFilterModal
                open={!!match.params.filtrosAvancados}
                tipoEndereco={TipoEnderecoEnum.LOGRADOURO}
                tabContext={TerritorioViewTabEnum.MAPA_DO_TERRITORIO}
                initialValues={values.filtrosAvancados}
                onModalClose={handleCloseFiltrosAvancados}
                onSubmit={(values) => handleFiltrosAvancadosSubmit(form, values)}
              />
            )}
          </Flow>
          <Flow direction='vertical' gap={0.5}>
            <SubmitButton handleSubmit={handleSubmit} size='medium'>
              <Flow direction='horizontal' gap={0.5}>
                <Icon icon='zoomOutline' />
                Buscar
              </Flow>
            </SubmitButton>
            <Button
              skin='outline'
              size='small'
              block
              onClick={() => handleReset(form)}
              disabled={isFilterDefault(values, defaultValues)}
            >
              <Flow direction='horizontal' gap={0.5}>
                <Icon icon='undo' />
                Voltar ao padrão
              </Flow>
            </Button>
          </Flow>
        </Flow>
      )}
      initialValues={initialValues}
      onSubmit={onSubmit}
      decorators={createTerritorioMapaBuscaFormCalculator}
      validate={validateVisualizacaoTerritorialForm<TerritorioMapaBuscaFormModel>(
        TerritorioViewTabEnum.MAPA_DO_TERRITORIO
      )}
    />
  )
}

const styles = {
  root: css`
    height: 100%;
    align-content: space-between;
  `,
}
